import { Box, ButtonBase, IconButton, Portal, Slider, Stack, Typography } from "@mui/material";
import { FC, MouseEvent, useEffect, useRef, useState } from "react";
import useSongPlayer from "../hooks/useSongPlayer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getPublicSongImageUrl, getPublicMp3Url } from "../utils/fileUtils";
import { formatDuration } from "../utils/formatUtils";
import { useWavesurfer } from '@wavesurfer/react';
import useModal from "../hooks/useModal";
import SongDialog from "./songDialog";
import useS3 from "../hooks/useS3";
import useQueryStringParams from "../hooks/useQueryStringParams";
import posthog from "posthog-js";

const ctx = document.createElement('canvas').getContext('2d');
const gradient = ctx?.createLinearGradient(0, 0, 1200, 0);
const gradient2 = ctx?.createLinearGradient(0, 0, 1200, 0);

if (gradient) {
	gradient.addColorStop(0, '#54113f')
	gradient.addColorStop(0.5, '#870a2a')
	gradient.addColorStop(1, '#834105')
}

if (gradient2) {
	gradient2.addColorStop(0, '#a9227e')
	gradient2.addColorStop(0.5, '#f03262')
	gradient2.addColorStop(1, '#f6831b')
};

const SongPlayer: FC = () => {
	const songPlayer = useSongPlayer();
	const modal = useModal();
	const wsContainerRef = useRef(null);
	const s3 = useS3();
	const { setQueryParam } = useQueryStringParams();

	const { wavesurfer } = useWavesurfer({
		container: wsContainerRef,
		height: 48,
		waveColor: 'rgba(153, 153, 153)',
		progressColor: gradient2,
		barWidth: 2,
		barGap: 2,
		barRadius: 4,
		url: songPlayer.song ? getPublicMp3Url(songPlayer.song) : ''
	});

	const audioRef = useRef<HTMLAudioElement>(null);

	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [volume, setVolume] = useState(1);
	const [isMuted, setIsMuted] = useState(false);
	const [volumeBeforeMute, setVolumeBeforeMute] = useState(1);
	const [isDownloading, setIsDownloading] = useState(false);

	useEffect(() => {
		if (audioRef.current) {
			audioRef.current.volume = volume;
		}
	}, [volume]);

	useEffect(() => {
		if (songPlayer.song) {
			console.log(`Now playing: ${songPlayer.song.title}`);
		}
	}, [songPlayer.song]);

	useEffect(() => {
		if (!audioRef || !audioRef.current) {
			return;
		}

		if (songPlayer.isPlaying) {
			audioRef.current.play();
		} else {
			audioRef.current.pause();
		}

		const handleWaveClick = (xPosition: number) => {
			if (audioRef.current) {
				audioRef.current.currentTime = xPosition * duration;
				setCurrentTime(audioRef.current.currentTime);
			}
		};

		wavesurfer?.on('click', handleWaveClick);
		wavesurfer?.on('dragend', handleWaveClick);

		return () => {
			wavesurfer?.un('click', handleWaveClick);
			wavesurfer?.un('dragend', handleWaveClick);
		};
	}, [duration, songPlayer.isPlaying, wavesurfer]);

	const viewDetail = async () => {
		if (songPlayer.song) {
			// If no other modals are open right now, just open the one for this song.
			if (modal.modals.length === 0) {
				setQueryParam('songId', songPlayer.song.id);

				posthog.capture('song_opened', {
					songId: songPlayer.song.id,
					songTitle: songPlayer.song.title,
					songAlbum: songPlayer.song.album?.name || '',
					origin: 'song_player'
				});

				await modal.show(
					900,
					{ prefix: 'fas', iconName: 'music' },
					songPlayer.song.title,
					<SongDialog song={songPlayer.song} />,
					false
				);

				setQueryParam('songId');
			} else if (modal.modals[0].title !== songPlayer.song.title) {
				// If a modal for a different song is open, close it first, then open the one for this song.
				modal.hide();

				setTimeout(async () => {
					if (songPlayer.song) {
						setQueryParam('songId', songPlayer.song.id);

						posthog.capture('song_opened', {
							songId: songPlayer.song.id,
							songTitle: songPlayer.song.title,
							songAlbum: songPlayer.song.album?.name || '',
							origin: 'song_player'
						});
		
						await modal.show(
							900,
							{ prefix: 'fas', iconName: 'music' },
							songPlayer.song.title,
							<SongDialog song={songPlayer.song} />,
							false
						);

						setQueryParam('songId');
					}
				}, 300);
			}
		}
	};

	const playPause = () => {
		if (songPlayer.isPlaying) {
			songPlayer.pause();
		} else {
			songPlayer.resume();
		}
	};

	// const stop = () => {
	// 	songPlayer.stop();
	// };

	// Update the current time as audio plays
	const handleTimeUpdate = () => {
		if (audioRef.current) {
			setCurrentTime(audioRef.current.currentTime);
			wavesurfer?.seekTo(audioRef.current.currentTime / duration);

			if (audioRef.current.currentTime >= duration) {
				playPause();
			}
		}
	};

	// Set the audio duration once metadata is loaded
	const handleLoadedMetadata = () => {
		if (audioRef.current) {
			setDuration(audioRef.current.duration);
		}
	};

	// Volume
	const handleVolumeChange = (event: Event, newValue: number | number[]) => {
		setVolume((newValue as number) / 100);

		if (newValue === 0) {
			setIsMuted(true);
		} else {
			setIsMuted(false);
		}
	};

	const toggleMute = () => {
		if (volume > 0) {
			setVolumeBeforeMute(volume);
			setVolume(0);
			setIsMuted(true);
		} else {
			setVolume(volumeBeforeMute);
			setIsMuted(false);
		}
	};

	const downloadSong = async (event: MouseEvent) => {
		event.stopPropagation();

		try {
			if (songPlayer && songPlayer.song) {
				// If no other modals are open right now, just open the one for this song.
				if (modal.modals.length === 0) {
					posthog.capture('song_opened', {
						songId: songPlayer.song.id,
						songTitle: songPlayer.song.title,
						songAlbum: songPlayer.song.album?.name || '',
						origin: 'song_player'
					});

					await modal.show(
						900,
						{ prefix: 'fas', iconName: 'music' },
						songPlayer.song.title,
						<SongDialog song={songPlayer.song} />,
						false
					);
				} else if (modal.modals[0].title !== songPlayer.song.title) {
					// If a modal for a different song is open, close it first, then open the one for this song.
					modal.hide();

					setTimeout(async () => {
						if (songPlayer.song) {
							posthog.capture('song_opened', {
								songId: songPlayer.song.id,
								songTitle: songPlayer.song.title,
								songAlbum: songPlayer.song.album?.name || '',
								origin: 'song_player'
							});

							await modal.show(
								900,
								{ prefix: 'fas', iconName: 'music' },
								songPlayer.song.title,
								<SongDialog song={songPlayer.song} />,
								false
							);
						}
					}, 300);
				} else {
					setIsDownloading(true);
					s3.getDownloadUrl(songPlayer.song.mp3FileS3Key)
						.then(resp => {
							const url = resp.url;

							if (songPlayer && songPlayer.song) {
								const link = document.createElement('a');
								link.href = url;
								link.download = songPlayer.song.mp3FileS3Key.split('/').pop() || 'download';
								document.body.appendChild(link);
								link.click();
								document.body.removeChild(link);

								posthog.capture('song_downloaded', {
									songId: songPlayer.song.id,
									songTitle: songPlayer.song.title,
									songAlbum: songPlayer.song.album?.name || '',
									origin: 'song_player'
								});
							}

							setIsDownloading(false);
						})
						.catch(err => {
							// toast.error(err.message)
							console.log(err.message);
							setIsDownloading(false);
						});
				}
			}
		}
		catch (err: any) {
			console.log('Error downloading s3 file:', err);
			setIsDownloading(false);
			// toast.error(err);
		}
	};

	return (
		<Portal container={() => document.getElementById('songPlayerPortal')}>
			{songPlayer.song && (
				<>
					<audio
						ref={audioRef}
						src={getPublicMp3Url(songPlayer.song)}
						preload="metadata"
						onTimeUpdate={handleTimeUpdate}
						onLoadedMetadata={handleLoadedMetadata}
					/>
					<Stack
						sx={{
							position: 'absolute',
							bottom: 0,
							left: 0,
							right: 0,
							pointerEvents: 'all'
						}}
					>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							spacing={2}
							sx={{
								borderTop: '1px solid rgba(255, 255, 255, 0.22)',
								px: 4,
								py: 1,
								color: 'white',
								backdropFilter: 'blur(6px) saturate(2)'
							}}
						>
							{/* Song Info */}
							<ButtonBase
								sx={{
									display: 'block',
									flexGrow: { xs: 1, sm: 1, md: 0, lg: 0, xl: 0 },
									flexShrink: 1,
									flexBasis: { xs: 'auto', sm: 'auto', md: '35%', lg: '20%', xl: '20%' },
									borderRadius: 1,
									p: 1,
									textAlign: 'left',
									transition: 'background-color 0.2s',
									'&:hover': {
										backgroundColor: 'rgba(255, 255, 255, 0.1)'
									}
								}}
								onClick={viewDetail}
							>
								<Stack direction="row" spacing={2} alignItems="center">
									<Box component="img" src={getPublicSongImageUrl(songPlayer.song)} height={50} />
									<Stack>
										<Typography variant="body1">{songPlayer.song.title}</Typography>
										<Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'GrayText' }}>
											{songPlayer.song.album && songPlayer.song.album.name && (
												<Typography variant="caption">{songPlayer.song.album.name}</Typography>
											)}
										</Stack>
									</Stack>
								</Stack>
							</ButtonBase>
							{/* Player */}
							<Stack direction="row" spacing={4} alignItems="center">
								<Stack direction="row" spacing={1} alignItems="center">
									{/* Kulig doesn't want a stop button here because it's confusing to users. People think it'll work like pause. */}
									{/* <IconButton size="small" sx={{ width: 32, height: 32 }} className="player-button" onClick={stop}>
										<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'stop' }} style={{ fontSize: 13, transform: 'translateY(1px)' }} />
									</IconButton> */}
									<IconButton size="large" sx={{ width: 52 }} className="player-button" onClick={playPause}>
										<FontAwesomeIcon icon={{ prefix: 'fas', iconName: songPlayer.isPlaying ? 'pause' : 'play' }} style={{ transform: songPlayer.isPlaying ? 'translateY(1px)' : 'translate(2px, 1px)' }} />
									</IconButton>
									<IconButton size="small" sx={{ width: 32, height: 32 }} className="player-button" onClick={downloadSong} disabled={isDownloading}>
										<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'download' }} style={{ fontSize: 13 }} />
									</IconButton>
								</Stack>
								<Box ref={wsContainerRef} sx={{ display: { xs: 'none', sm: 'none', md: 'block', lg: 'block', xl: 'block' }, width: { md: 300, lg: 450, xl: 600 } }} />
								<Stack direction="row" spacing={1} alignItems="center" display={{ xs: 'none', sm: 'flex', md: 'flex', lg: 'flex', xl: 'flex' }}>
									<Typography variant="body2" color="white" style={{ fontFeatureSettings: `'tnum'` }}>{formatDuration(currentTime)}</Typography>
									<Typography variant="body2" color="white" style={{ fontFeatureSettings: `'tnum'` }}>/</Typography>
									<Typography variant="body2" color="white" style={{ fontFeatureSettings: `'tnum'` }}>{formatDuration(duration)}</Typography>
								</Stack>
							</Stack>
							{/* Volume Controls */}
							<Stack direction="row" justifyContent='flex-end' alignItems="center" flex="0 0 20%" display={{ xs: 'none', sm: 'none', md: 'none', lg: 'flex', xl: 'flex' }}>
								<Stack direction="row" spacing={1} alignItems="center" width={240}>
									<IconButton size="small" sx={{ width: 40, height: 40 }} onClick={toggleMute}>
										<FontAwesomeIcon icon={{ prefix: 'fas', iconName: isMuted ? 'volume-xmark' : 'volume-high' }} />
									</IconButton>
									<Slider
										size="small"
										value={typeof volume === 'number' ? volume * 100 : 0}
										onChange={handleVolumeChange}
									/>
								</Stack>
							</Stack>
						</Stack>
					</Stack>
				</>
			)}
		</Portal>
	);
};

export default SongPlayer;
